import React, { useState, useEffect, useCallback } from 'react'
import { FileText, Tag, Calculator, Layers, Settings } from 'lucide-react'
import { AnimatedTabContent } from '@/components/ui/animated-tab-content'
import QuestionManagement from '@/features/questions/components/question-management'
import ScoreLabelManagement from '@/features/scoring-labels/components/score-label-management'
import DomainManagement from '@/features/settings/components/domain-management'
import MaturityLevelsManagement from '@/features/settings/components/maturity-levels-management'
import ScoringLogicExplanation from '@/features/settings/components/scoring-logic-explanation'
import { useEngineDomains } from '@/features/domains/hooks'
import { useEngineMaturityLevels } from '@/features/maturity-levels/hooks'
import { api } from '@/lib/api-client'
import { useSearchParams } from 'react-router-dom'
import { useAuthorization } from '@/features/authorization/AuthorizationContext'
import { useAuthentication } from '@/features/auth/AuthenticationContext'

const AssessmentEnginePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { actions: userActions } = useAuthorization()
  const { user } = useAuthentication()
  const isAdmin = user?.is_administrator === true

  const canView = (actions: string[] | null) => {
    if (!actions) return true // null means always visible (e.g. Scoring Logic)
    return isAdmin || actions.some(a => userActions.has(a))
  }

  const tabs = [
    { id: 'maturity-levels' as const, label: 'Maturity Levels', icon: Layers, requiredActions: ['view_maturity_levels'] as string[] | null },
    { id: 'domains' as const, label: 'Domains', icon: Settings, requiredActions: ['view_domains'] as string[] | null },
    { id: 'questions' as const, label: 'Questions', icon: FileText, requiredActions: ['view_questions'] as string[] | null },
    { id: 'score-labels' as const, label: 'Score Labels', icon: Tag, requiredActions: ['view_scoring_labels'] as string[] | null },
    { id: 'scoring-logic' as const, label: 'Scoring Logic', icon: Calculator, requiredActions: null as string[] | null },
  ]

  const availableTabs = tabs.filter(tab => canView(tab.requiredActions))

  type TabId = typeof tabs[number]['id']
  const validTabIds = availableTabs.map(t => t.id)
  const tabParam = searchParams.get('tab') as TabId | null
  const activeTab: TabId = tabParam && validTabIds.includes(tabParam as any) ? tabParam : (validTabIds[0] ?? 'maturity-levels')
  const setActiveTab = useCallback((tab: TabId) => {
    setSearchParams({ tab }, { replace: true })
  }, [setSearchParams])
  const [stats, setStats] = useState({
    questions: 0,
    scoreLabels: 0,
  })

  // Fetch domains & maturity levels via hooks
  const { data: domainsData } = useEngineDomains()
  const { data: maturityLevelsData } = useEngineMaturityLevels()

  const domainsList = Array.isArray(domainsData) ? domainsData : domainsData?.data || []
  const mlList = Array.isArray(maturityLevelsData) ? maturityLevelsData : maturityLevelsData?.data || []
  const domainsCount = Array.isArray(domainsList) ? domainsList.length : 0
  const mlCount = Array.isArray(mlList) ? mlList.length : 0

  useEffect(() => {
    const loadStats = async () => {
      try {

        const [questionsRes, scoreLabelsRes] = await Promise.all([
          api.assessmentEngine.questions.getAll().catch(() => ({ data: { data: [] } })),
          api.assessmentEngine.scoreLabels.getAll().catch(() => ({ data: { data: [] } }))
        ])

        setStats({
          questions: questionsRes.data?.total || questionsRes.data?.items?.length || questionsRes.data?.data?.length || 0,
          scoreLabels: scoreLabelsRes.data?.data?.length || 0,
        })
      } catch (error) {
        console.error('Error loading stats:', error)
      }
    }

    loadStats()
  }, [])

  const countMap: Record<string, number | null> = {
    'maturity-levels': mlCount,
    'domains': domainsCount,
    'questions': stats.questions,
    'score-labels': stats.scoreLabels,
    'scoring-logic': null,
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Assessment Engine</h1>
        <p className="text-gray-600">Manage the assessment framework, questions, and scoring criteria</p>
      </div>

      {/* Tab Navigation — underline style matching General page */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {availableTabs.map((tab) => {
            const Icon = tab.icon
            const count = countMap[tab.id]
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Icon className="h-4 w-4 inline mr-2" />
                {tab.label}
                {count != null && (
                  <span className="ml-1.5 text-xs font-normal rounded-full bg-gray-100 text-gray-600 px-2 py-0.5">
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <AnimatedTabContent activeTab={activeTab}>
        {activeTab === 'scoring-logic' && (
          <ScoringLogicExplanation />
        )}

        {activeTab === 'questions' && <QuestionManagement />}
        {activeTab === 'score-labels' && <ScoreLabelManagement />}
        {activeTab === 'domains' && <DomainManagement />}
        {activeTab === 'maturity-levels' && <MaturityLevelsManagement />}
      </AnimatedTabContent>
    </div>
  )
}

export default AssessmentEnginePage
